import React, { useState, useRef } from 'react';
import { SurveyPDF } from 'survey-pdf';
import { slk } from 'survey-core';
import flatMap from 'lodash/flatMap';

import { FaDownload } from 'react-icons/fa';
import { Spinner, Stack, Form } from 'react-bootstrap';

import api from 'api';
import useUser from 'hooks/useUser';
import useFeedback from 'hooks/useFeedback';
import useCurrentApplication from 'hooks/useCurrentApplication';

import SVButton from 'components/elements/SVButton';

import {
  surveyPdfOptions,
  mapQuestions,
  onRenderHeader,
  onRenderFooter,
  onRenderQuestion,
  stripHtmlNotes,
} from './utils';

export interface SurveyPDFExportProps {
  surveyVersionId: number;
  overrider?: Record<string, unknown>;
  formType?: 'PRQ' | 'MeetingsAndRounds' | string;
}

export type ExportMode = 'full' | 'core';

const SurveyPDFExport: React.FC<SurveyPDFExportProps> = ({
  surveyVersionId,
  overrider,
  formType = 'PRQ',
}) => {
  const { setError } = useFeedback();
  const hasChat = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [exportMode, setExportMode] = useState<ExportMode>('full');
  const { isAdmin } = useUser();
  const {
    appData: { id: appId, bcqpBurnCenterName },
    userTypeId,
  } = useCurrentApplication();

  const handlePDFDownload = async () => {
    setLoading(true);
    slk(process.env.REACT_APP_SURVEYJS_LICENSE_KEY || 'your-license-key-here');

    try {
      const {
        data: { model: rawModel, data },
      } = await api.getReport({
        applicationId: appId,
        surveyVersionId,
        userTypeId,
        includeChat: isAdmin ? null : hasChat.current?.checked ?? false,

      });
      const model = stripHtmlNotes(rawModel);

      if (exportMode === 'core') {
        model.pages = model.pages.filter((page: any) => {
          const pageName = page.name || '';
          const sectionId = parseInt(pageName.split('_')[0], 10);

          if (formType === 'PRQ') {
            // PRQ Core Report: Initial Questions, Case Summaries, Appendix - Tables only
            // Section 0, 20, 21_2 (exclude 21_1 which is Appendix - Files)
            if (sectionId === 0 || sectionId === 20) {
              return true;
            }
            if (sectionId === 21) {
              // Only include Appendix - Tables (21_2), not Appendix - Files (21_1)
              return pageName === '21_2';
            }
            return false;
          }

          if (formType === 'MeetingsAndRounds') {
            // M&R Core Report: Section 1 = Meetings, Section 2 = Clinical Rounds
            return sectionId === 1 || sectionId === 2;
          }

          return true;
        });
      }

      const pagesBySectionId = model.pages.reduce((acc: any, page: any) => {
        const pageName = page.name || '';
        const sectionId = parseInt(pageName.split('_')[0], 10);
        if (!acc[sectionId]) {
          acc[sectionId] = [];
        }
        acc[sectionId].push(page);
        return acc;
      }, {});

      model.pages = Object.keys(pagesBySectionId).map((sectionId) => {
        const sectionPages = pagesBySectionId[sectionId];
        if (sectionPages.length === 1) {
          return sectionPages[0];
        }

        const firstPage = sectionPages[0];
        const mergedElements: any[] = [];

        sectionPages.forEach((page: any, index: number) => {
          if (index > 0) {
            const panel: any = {
              type: 'panel',
              name: `${page.name}_panel`,
              title: page.title || '',
              description: page.description || '',
              elements: page.elements || [],
            };
            mergedElements.push(panel);
          } else if (page.elements) {
            mergedElements.push(...page.elements);
          }
        });

        return {
          ...firstPage,
          elements: mergedElements,
        };
      });

      const pdf = new SurveyPDF(model, surveyPdfOptions);
      pdf.data = data;
      pdf.mode = 'display';

      pdf.onRenderHeader.add(onRenderHeader(bcqpBurnCenterName));

      pdf.onRenderFooter.add(onRenderFooter);

      const allQuestions = mapQuestions(
        flatMap(model.pages, ({ elements }) => elements),
      );

      pdf.onRenderQuestion.add(onRenderQuestion(allQuestions, { overrider }));

      const modeLabel = exportMode === 'core' ? 'Core Report' : 'Full Report';
      await pdf.save(`${modeLabel} - ${bcqpBurnCenterName}.pdf`);
    } catch (err) {
      if (err instanceof Error) {
        setError(`Error downloading Pdf, message: ${err.message}, stack: ${err.stack}`);
      } else {
        setError(`Error downloading Pdf, error: ${err}`);
      }
    }

    setLoading(false);
  };

  return (
    <Stack direction="horizontal" gap={4}>
      <Stack direction="horizontal" gap={2}>
        <span style={{ fontWeight: 500 }}>Export Mode:</span>
        <Form.Select
          size="sm"
          value={exportMode}
          onChange={(e) => setExportMode(e.target.value as ExportMode)}
          disabled={loading}
          style={{ width: 'auto' }}
        >
          <option value="full">Full Report</option>
          <option value="core">Core Report</option>
        </Form.Select>
      </Stack>
      {!isAdmin && (
        <Stack direction="horizontal" gap={2}>
          <span>Include Chat</span>
          <input type="checkbox" ref={hasChat} disabled={loading} />
        </Stack>
      )}
      <SVButton onClick={handlePDFDownload} disabled={loading}>
        <Stack direction="horizontal" gap={3}>
          {loading ? <Spinner size="sm" animation="border" /> : <FaDownload />}
          Download PDF
        </Stack>
      </SVButton>
    </Stack>
  );
};

export default SurveyPDFExport;
